import styled, { css, keyframes } from 'styled-components';
import { ThemeType } from '../../types/command';

// Animations
const fadeInScale = keyframes`
  from {
    transform: translateY(6px) scale(0.98);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
`;

// Terminal header - redesigned with window controls
export const TerminalHeader = styled.div`
  background-color: #333;
  color: white;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  user-select: none;
  min-height: 40px;
`;

// macOS-style window dots container
export const WindowControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Individual dot
export const WindowDot = styled.span<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  display: inline-block;
`;

// Header title (center)
export const HeaderTitle = styled.span`
  flex: 1;
  text-align: center;
  font-size: 13px;
  opacity: 0.9;
  letter-spacing: 0.3px;
`;

// Header decorative actions (right side)
export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  opacity: 0.5;
  font-size: 14px;
`;

export const HeaderActionIcon = styled.span`
  cursor: default;
  line-height: 1;
`;

// Terminal body
export const TerminalBody = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  color: white;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.3);
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

// Terminal input area
export const TerminalInput = styled.div`
  display: flex;
  align-items: center;
  background-color: transparent;
  padding: 12px 20px;
  border-top: 1px solid #444;
  min-height: 48px;
`;

// Prompt
export const PromptSpan = styled.span`
  margin-right: 10px;
  font-weight: bold;
  white-space: nowrap;
  user-select: none;
  color: inherit;
`;

// Input field
export const InputField = styled.input`
  background: transparent;
  border: none;
  outline: none;
  flex: 1;
  color: inherit;
  font-family: inherit;
  font-size: inherit;
  min-height: 24px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

// Output line
export const OutputLine = styled.div`
  margin: 6px 0;
  line-height: 1.6;
  letter-spacing: 0.3px;
  white-space: pre-wrap;
  word-break: break-word;
  animation: ${fadeInScale} 0.2s ease-out forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// Divider line
export const DividerLine = styled(OutputLine)`
  font-weight: 300;
  margin: 6px 0 8px;
  line-height: 0.5;
  letter-spacing: -0.2px;
  opacity: 0.7;
  font-size: 0.95em;
`;

// Error line
export const ErrorLine = styled(OutputLine)``;

// Success line
export const SuccessLine = styled(OutputLine)``;

// Info line
export const InfoLine = styled(OutputLine)``;

// Status bar at the bottom
export const StatusBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px 20px;
  font-size: 12px;
  opacity: 0.7;
  user-select: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  min-height: 24px;
  flex-shrink: 0;

  @media (max-width: 480px) {
    display: none;
  }
`;

export const StatusItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
`;

export const StatusDivider = styled.span`
  opacity: 0.3;
  &::after {
    content: '|';
  }
`;

// Theme styles
const themeStyles = {
  cmd: css`
    background-color: #0C0C0C;
    color: #CCCCCC;
    font-family: 'Cascadia Mono', 'Consolas', 'Lucida Console', monospace;
    font-size: 14px;

    ${TerminalHeader} {
      background-color: #0C0C0C;
      border-bottom: 1px solid #333333;
    }

    ${PromptSpan} {
      color: #CCCCCC !important;
    }

    ${InputField} {
      color: #CCCCCC;
    }

    ${SuccessLine} {
      color: #16C60C;
    }

    ${ErrorLine} {
      color: #E74856;
    }

    ${InfoLine} {
      color: #3B78FF;
    }

    ${DividerLine} {
      color: #767676;
    }

    ${StatusBar} {
      background-color: rgba(20, 20, 20, 0.8);
      color: #767676;
    }

    --terminal-black: #0C0C0C;
    --terminal-blue: #0037DA;
    --terminal-green: #13A10E;
    --terminal-cyan: #3A96DD;
    --terminal-red: #C50F1F;
    --terminal-magenta: #881798;
    --terminal-yellow: #C19C00;
    --terminal-white: #CCCCCC;
    --terminal-bright-black: #767676;
    --terminal-bright-blue: #3B78FF;
    --terminal-bright-green: #16C60C;
    --terminal-bright-cyan: #61D6D6;
    --terminal-bright-red: #E74856;
    --terminal-bright-magenta: #B4009E;
    --terminal-bright-yellow: #F9F1A5;
    --terminal-bright-white: #F2F2F2;
  `,

  powershell: css`
    background-color: #012456;
    color: #EEEDF0;
    font-family: 'Cascadia Code PL', 'Cascadia Mono', 'Consolas', monospace;
    font-size: 14px;

    ${TerminalHeader} {
      background-color: #012456;
      border-bottom: 1px solid #0A3172;
    }

    ${PromptSpan} {
      color: #FFFF00 !important;
    }

    ${InputField} {
      color: #EEEDF0;
    }

    ${SuccessLine} {
      color: #2CEC26;
    }

    ${ErrorLine} {
      color: #F14C4C;
    }

    ${InfoLine} {
      color: #59A2FF;
    }

    ${DividerLine} {
      color: #8A8A8A;
    }

    ${StatusBar} {
      background-color: rgba(1, 30, 70, 0.8);
      color: #8A8A8A;
    }

    --terminal-black: #012456;
    --terminal-blue: #0037DA;
    --terminal-green: #008000;
    --terminal-cyan: #0089B5;
    --terminal-red: #C50F1F;
    --terminal-magenta: #7F007F;
    --terminal-yellow: #DCDC00;
    --terminal-white: #EEEDF0;
    --terminal-bright-black: #8A8A8A;
    --terminal-bright-blue: #59A2FF;
    --terminal-bright-green: #2CEC26;
    --terminal-bright-cyan: #00DDFF;
    --terminal-bright-red: #F14C4C;
    --terminal-bright-magenta: #FF00FF;
    --terminal-bright-yellow: #FFFC67;
    --terminal-bright-white: #FFFFFF;
  `,

  ubuntu: css`
    background-color: #300A24;
    color: #FFFFFF;
    font-family: 'Ubuntu Mono', 'Droid Sans Mono', monospace;
    font-size: 14px;

    ${TerminalHeader} {
      background-color: #300A24;
      border-bottom: 1px solid #3F1B37;
    }

    ${PromptSpan} {
      color: #4CBB17 !important;
      font-weight: bold !important;
    }

    ${InputField} {
      color: #FFFFFF;
    }

    ${SuccessLine} {
      color: #4CBB17;
    }

    ${ErrorLine} {
      color: #FF6B6B;
    }

    ${InfoLine} {
      color: #7EBAE4;
    }

    ${DividerLine} {
      color: #A3A3A3;
    }

    ${StatusBar} {
      background-color: rgba(40, 8, 30, 0.8);
      color: #A3A3A3;
    }

    --terminal-black: #2E3436;
    --terminal-blue: #3465A4;
    --terminal-green: #4E9A06;
    --terminal-cyan: #06989A;
    --terminal-red: #CC0000;
    --terminal-magenta: #75507B;
    --terminal-yellow: #C4A000;
    --terminal-white: #D3D7CF;
    --terminal-bright-black: #555753;
    --terminal-bright-blue: #729FCF;
    --terminal-bright-green: #8AE234;
    --terminal-bright-cyan: #34E2E2;
    --terminal-bright-red: #EF2929;
    --terminal-bright-magenta: #AD7FA8;
    --terminal-bright-yellow: #FCE94F;
    --terminal-bright-white: #EEEEEC;
  `
};

// Terminal container
export const TerminalContainer = styled.div<{ $theme: ThemeType }>`
  width: 80%;
  max-width: 1000px;
  height: 70vh;
  background-color: rgba(0, 0, 0, 0.85);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.6);
  font-family: 'Fira Code', 'Consolas', monospace;
  z-index: 100;

  @media (max-width: 768px) {
    width: 100%;
    height: 80vh;
    max-height: calc(100vh - 40px);
    border-radius: 8px;
    margin: 0 10px;
    margin-top: 20px;
  }

  @media (max-width: 480px) {
    width: 100%;
    height: 85vh;
    max-height: calc(100vh - 30px);
    border-radius: 6px;
    margin: 0 5px;
    margin-top: 20px;
    font-size: 13px;
  }

  ${props => themeStyles[props.$theme]}
`;
